<!-- src/components/Modal.vue -->
<template>
  <div v-if="isOpen" class="modal-backdrop">
    <div v-if="isInitError" class="modal-container" @click.stop>
      <div ref="modalContent" class="modal-content">
        <div class="errorform">
          <!-- エラーメッセージの表示エリア -->
          <div v-if="errorMessage" class="error-message">
            {{ errorMessage }}
          </div>
        </div>
      </div>
      <!-- モーダルフッター -->
      <div v-if="errorMessage" class="modal-footer">
        <div class="com_mgt10">
          <button class="com_s_button1" @click="onClosed">
            <span class="text">
              <span class="text__item">閉じる</span>
              <span class="text__item" aria-hidden="true">閉じる</span>
            </span>
          </button>
        </div>
      </div>
    </div>
    <div v-else class="modal-container" @click.stop>
      <template v-if="isDataFetched">
        <div ref="modalContent" class="modal-content" id="contents1">
          <!-- エラーメッセージの表示エリア -->
          <div
            v-if="errorMessage"
            class="error-message"
            style="white-space: pre-wrap"
          >
            {{ errorMessage }}
          </div>
          <!-- タイトル -->
          <h1>{{ contentsData.title }}</h1>
          <!-- 本文 -->
          <div class="ql-editor" v-html="sanitizedContent"></div>
          <!-- 画像ファイル -->
          <div
            v-if="contentsData.contentsImgList.length > 0"
            class="com_d_content1"
          >
            <div
              v-for="imgFile in contentsData.contentsImgList"
              :key="imgFile.rowNo"
            >
              <img
                :src="`/${imgFile.imgPath}/${imgFile.imgName}`"
                :alt="imgFile.imgExp"
              />
            </div>
          </div>

          <div v-if="isExistsLinkAreaItem" class="com_links1">
            <!-- リンク -->
            <div v-if="contentsData.contentsLinkList.length > 0" class="item1">
              <div>
                <h3>リンク</h3>
              </div>
              <div>
                <ul
                  v-for="link in contentsData.contentsLinkList"
                  :key="link.rowNo"
                  class="list1"
                >
                  <li>
                    <a
                      :href="`${link.url}`"
                      class="com_link1"
                      target="_blank"
                      @click="regLinkClick(link.url)"
                    >
                      {{ link.caption || link.url }}
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <!-- 添付ファイル -->
            <div
              v-if="contentsData.contentsTempFileList.length > 0"
              class="item1"
            >
              <div>
                <h3>文書ファイル</h3>
                <a
                  href="#"
                  class="com_dl_button1"
                  @click.prevent="
                    downloadFiles(
                      contentsData.categoryId,
                      contentsData.contentsId,
                      ''
                    )
                  "
                >
                  <span>全データダウンロード</span>
                </a>
              </div>
              <div>
                <ul
                  v-for="tmpFile in contentsData.contentsTempFileList"
                  :key="tmpFile.rowNo"
                  class="list2"
                >
                  <li>
                    <a
                      :href="`/${tmpFile.tempFilePath}/${tmpFile.tempFileName}`"
                      class="com_link1"
                      target="_blank"
                      @click="regFileLinkClick(tmpFile.tempFileName)"
                    >
                      {{ tmpFile.tempFileName }}
                    </a>
                    <span v-if="tmpFile.tempFileSize" class="sub1">
                      ({{ tmpFile.tempFileSize }})
                    </span>
                    <a
                      href="#"
                      class="com_dl_button2"
                      @click.prevent="
                        downloadFiles(
                          contentsData.categoryId,
                          contentsData.contentsId,
                          tmpFile.tempFileName
                        )
                      "
                    >
                      <span>ダウンロード</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <!-- モーダルフッター -->
        <div v-if="isCheckPaternConfirm" class="modal-footer">
          <div class="com_checkbox1">
            <input
              type="checkbox"
              id="checkbox"
              v-model="isChecked"
              @change="onChangeCheckState"
            />
            <label for="checkbox" class="form-check-label">確認した</label>
          </div>
          <div class="com_mgt10">
            <button v-if="!isChecked" class="com_s_button1" disabled>
              <span class="text">閉じる</span>
            </button>
            <button v-else class="com_s_button1" @click="onClosed">
              <span class="text">
                <span class="text__item">閉じる</span>
                <span class="text__item" aria-hidden="true">閉じる</span>
              </span>
            </button>
          </div>
        </div>
        <div v-else class="modal-footer">
          <div class="com_checkbox1">
            <input
              type="checkbox"
              id="checkbox"
              v-model="isChecked"
              @change="onChangeCheckState"
            />
            <label for="checkbox" class="form-check-label">
              次回以降表示しない
            </label>
          </div>
          <div class="com_mgt10">
            <button class="com_s_button1" @click="onClosed">
              <span class="text">
                <span class="text__item">閉じる</span>
                <span class="text__item" aria-hidden="true">閉じる</span>
              </span>
            </button>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  watch,
  onMounted,
  computed,
  onUnmounted,
} from "vue";
import { useProcessingStore } from "../../store/useProcessingStore";
import apiAuthClient from "@/util/apiClientAuth";
import apiDownloadClient from "@/util/apiClientDownload";
import { AppConstants } from "@/constants/AppConstants";
import {
  formatMessage,
  getTargetDate,
  outputDebugLogs,
  sanitizeInput,
  isTokenAlive,
  regLinkClickLog,
  regFileLinkClickLog,
  commonHandleAxiosError,
} from "@/Common";
import messages from "@/assets/messages.json";
import { AxiosError } from "axios";
import { useRouter } from "vue-router";
import { useUserStore } from "@/store/userStore";

// ContentsLinkListModel
interface ContentsLinkListModel {
  rowNo: number;
  caption: string;
  url: string;
}

// ContentsTempFileListModel
interface ContentsTempFileListModel {
  rowNo: number;
  tempFileExp: string;
  tempFilePath: string;
  tempFileName: string;
  tempFileSize: string;
}

// ContentsImgListModel
interface ContentsImgListModel {
  rowNo: number;
  imgExp: string;
  imgPath: string;
  imgName: string;
}

// Main Data Model
interface DataModel {
  categoryId: string;
  contentsId: string;
  title: string;
  keisaiKikanStart: string;
  keisaiKikanEnd: string;
  contentsBody: string;
  contentsLinkList: ContentsLinkListModel[];
  contentsTempFileList: ContentsTempFileListModel[];
  contentsImgList: ContentsImgListModel[];
  checkPatern: string;
  checkDispTarget: string;
}

class LoginPopupModalConstants {
  // API-URL
  // ログイン後ポップアップ通知データ取得
  static readonly API_URL_GET_POPUP_DATA = "/api/LoginPopup/GetPopupData";
  // ログイン後ポップアップ通知確認履歴登録・削除
  static readonly API_URL_REG_POPUP_CHECK_HISTORY =
    "/api/LoginPopup/RegPopupCheckHistory";

  // チェックパターン
  static readonly CHECK_PATERN_HIDE_NEXT_TIME = "1"; // 次回以降表示しない
  static readonly CHECK_PATERN_CONFIRM = "2"; // 確認した

  // 画面名
  static readonly SCREEN_NAME = "ログイン後ポップアップ通知";
}

export default defineComponent({
  name: "LoginPopupModalComponent",
  props: {
    isOpen: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    categoryId: {
      type: String as PropType<string>,
      required: true,
    },
    contentsId: {
      type: String as PropType<string>,
      required: true,
    },
    isPreview: {
      type: Boolean as PropType<boolean>,
    },
  },
  emits: ["closed"],
  setup(props, { emit }) {
    const isInitError = ref(true); // 初期表示時のエラーフラグ
    const isDataFetched = ref(false); // データが取得済みかどうかを追跡
    const categoryIdRef = ref("");
    const contentsIdRef = ref("");
    const isPreviewMode = ref(false);
    const processingStore = useProcessingStore();
    const errorMessage = ref("");
    const agCode = ref(""); // AGコード
    const userId = ref(""); // ユーザーID
    const pseudoAgCode = ref(""); // 疑似ログインAGコード
    const pseudoUserId = ref(""); // 疑似ログインユーザーID
    const serchAgCode = ref(""); // 検索用AGコード
    const serchUserId = ref(""); // 検索用ユーザーID
    const sanitizedContent = ref(""); //サニタイジングされた本文
    const isChecked = ref(false);
    const router = useRouter();
    const { logoutUser } = useUserStore();
    const modalContent = ref<HTMLElement | null>(null);

    /**
     * ポップアップ通知データの定義
     */
    const contentsData = ref<DataModel>({
      categoryId: "",
      contentsId: "",
      title: "",
      keisaiKikanStart: "",
      keisaiKikanEnd: "",
      contentsBody: "",
      contentsLinkList: [],
      contentsTempFileList: [],
      contentsImgList: [],
      checkPatern: "",
      checkDispTarget: "",
    });

    // 初期処理
    onMounted(async () => {
      // 処理中フラグON
      processingStore.isProcessing = true;

      try {
        isInitError.value = true;
        isDataFetched.value = false;

        // パラメーター設定
        categoryIdRef.value = props.categoryId;
        contentsIdRef.value = props.contentsId;
        isPreviewMode.value = props.isPreview ?? true;

        // ローカルストレージからユーザー情報を取得して内部変数に展開
        const userInfo = localStorage.getItem(AppConstants.USER_SESSION_NAME);
        if (userInfo) {
          const parsedUserInfo = JSON.parse(userInfo);
          agCode.value = parsedUserInfo.agCode;
          userId.value = parsedUserInfo.userId;
          pseudoAgCode.value = parsedUserInfo.pseudoAgCode;
          pseudoUserId.value = parsedUserInfo.pseudoUserId;
        }

        if (pseudoUserId.value == "") {
          // 疑似ログインIDが空の場合は、疑似ログインしていないとみなす
          serchAgCode.value = agCode.value;
          serchUserId.value = userId.value;
        } else {
          outputDebugLogs("疑似ログイン状態");
          serchAgCode.value = pseudoAgCode.value;
          serchUserId.value = pseudoUserId.value;
        }

        // ログイン後ポップアップ通知データ取得
        if (await getPopupData()) {
          isInitError.value = false;
          isDataFetched.value = true;
        }
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    });

    watch(
      [() => props.categoryId, () => props.contentsId],
      async ([newCategoryId, newContentsId]) => {
        // 処理中フラグON
        processingStore.isProcessing = true;

        try {
          isInitError.value = true;
          isDataFetched.value = false;

          // パラメーター設定
          categoryIdRef.value = newCategoryId;
          contentsIdRef.value = newContentsId;

          // エラーメッセージ初期化
          errorMessage.value = "";

          // ログイン後ポップアップ通知データ取得
          if (await getPopupData()) {
            isInitError.value = false;
            isDataFetched.value = true;
          }
        } finally {
          // 処理中フラグOFF
          processingStore.isProcessing = false;
        }
      }
    );

    onUnmounted(() => {
      // 画面の縦スクロールを有効化
      document.body.classList.remove("modal-open");
    });

    /**
     * モーダルを閉じる
     */
    const onClosed = () => {
      // 何か処理中の場合は処理しない
      if (processingStore.isProcessing) {
        return;
      }
      emit("closed");
    };

    /**
     * チェックボックスの変更時
     */
    const onChangeCheckState = async () => {
      // プレビューの場合は処理しない
      if (isPreviewMode.value) {
        return;
      }

      // 何か処理中の場合は処理しない
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      try {
        // エラーメッセージ初期化
        errorMessage.value = "";

        // トークン有効性チェック
        if ((await isTokenAlive()) == false) {
          // トークンが無効な場合はログイン画面へ
          logoutUser();
          alert(messages.ERR_USER001);
          router.push("/login");
          return;
        }

        // ポップアップ通知確認履歴登録・削除
        if (!(await regPopupCheckHistory())) {
          errorMessage.value = formatMessage(
            messages.ERR_SYS003,
            "ポップアップ通知確認履歴登録・削除"
          );
        }
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * ログイン後ポップアップ通知データ取得
     */
    const getPopupData = async (): Promise<boolean> => {
      try {
        // ログイン後ポップアップ通知データ取得APIを実行
        const response = await apiAuthClient.post(
          LoginPopupModalConstants.API_URL_GET_POPUP_DATA,
          {
            systemKbn: process.env.VUE_APP_SYSTEM_KBN,
            agCode: serchAgCode.value,
            userId: serchUserId.value,
            targetDate: getTargetDate(""),
            categoryId: categoryIdRef.value,
            contentId: contentsIdRef.value,
          }
        );

        // レスポンスからポップアップ通知データを取得
        const data = response.data;
        contentsData.value = data;

        // 本文をサニタイジングする
        sanitizedContent.value = sanitizeInput(contentsData.value.contentsBody);
        // チェックボックスを未チェックにする
        isChecked.value = false;
        return true;
      } catch (error: unknown) {
        handleAxiosError(error);
        return false;
      }
    };

    /**
     * ログイン後ポップアップ通知確認履歴登録・削除
     */
    const regPopupCheckHistory = async (): Promise<boolean> => {
      try {
        // ログイン後ポップアップ通知確認履歴登録・削除APIを実行
        await apiAuthClient.post(
          LoginPopupModalConstants.API_URL_REG_POPUP_CHECK_HISTORY,
          {
            systemKbn: process.env.VUE_APP_SYSTEM_KBN,
            agCode: serchAgCode.value,
            userId: serchUserId.value,
            categoryId: categoryIdRef.value,
            contentsId: contentsIdRef.value,
            isChecked: isChecked.value,
          }
        );

        return true;
      } catch (error: unknown) {
        const axiosError = error as AxiosError;
        outputDebugLogs("Error: " + axiosError.message);
        // スクロールを一番上に戻す
        scrollToTop();
        return false;
      }
    };

    /**
     * AxiosErrorを処理します。
     * @param error エラー情報
     */
    const handleAxiosError = (error: unknown) => {
      errorMessage.value = commonHandleAxiosError(error, false);
    };

    /**
     * チェックパターンが確認したか
     */
    const isCheckPaternConfirm = computed(() => {
      return (
        contentsData.value.checkPatern ==
        LoginPopupModalConstants.CHECK_PATERN_CONFIRM
      );
    });

    /**
     * リンクエリアのデータが存在するか
     */
    const isExistsLinkAreaItem = computed(() => {
      return (
        contentsData.value.contentsLinkList.length > 0 ||
        contentsData.value.contentsTempFileList.length > 0
      );
    });

    /**
     * ファイルダウンロード
     * @param ctgyId - カテゴリID
     * @param ctsId - コンテンツID
     */
    const downloadFiles = async (
      ctgyId: string,
      ctsId: string,
      flName: string
    ) => {
      // 何か処理中の場合は処理しない
      if (processingStore.isProcessing) {
        return;
      }

      // 処理中フラグON
      processingStore.isProcessing = true;

      try {
        // エラーメッセージ初期化
        errorMessage.value = "";

        // トークン有効性チェック
        if ((await isTokenAlive()) == false) {
          // トークンが無効な場合はログイン画面へ
          logoutUser();
          alert(messages.ERR_USER001);
          router.push("/login");
          return;
        }

        // ファイルダウンロードAPI
        const response = await apiDownloadClient.post("/api/download", {
          systemKbn: process.env.VUE_APP_SYSTEM_KBN,
          userId: userId.value,
          agCode: agCode.value,
          categoryId: ctgyId,
          contentId: ctsId,
          fileName: flName,
          targetDate: getTargetDate(""),
          pseudoUserId: pseudoUserId.value,
          pseudoAgCode: pseudoAgCode.value,
          screenName: LoginPopupModalConstants.SCREEN_NAME,
        });

        // Content-Dispositionヘッダーからファイル名を取得
        const contentDisposition = response.headers["content-disposition"];
        let fileName = "downloaded_file";
        if (contentDisposition) {
          const match = contentDisposition.match(/filename\*=UTF-8''(.+)/);
          if (match && match[1]) {
            fileName = decodeURIComponent(match[1]);
          }
        }

        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
        window.URL.revokeObjectURL(link.href);
      } catch (error) {
        // ダウンロードAPIエラー
        outputDebugLogs("Error downloading files: " + error);

        // エラーメッセージを表示
        errorMessage.value = formatMessage(
          messages.ERR_SYS003,
          "ファイルダウンロード"
        );

        // スクロールを一番上に戻す
        scrollToTop();
      } finally {
        // 処理中フラグOFF
        processingStore.isProcessing = false;
      }
    };

    /**
     * リンククリックのログを出力します
     * @param linkUrl リンクURL
     */
    const regLinkClick = (linkUrl: string) => {
      regLinkClickLog(
        linkUrl,
        userId.value,
        agCode.value,
        pseudoAgCode.value,
        pseudoUserId.value,
        contentsData.value.contentsId,
        LoginPopupModalConstants.SCREEN_NAME
      );
    };

    /**
     * ファイルリンククリックのログを出力します
     * @param fileName ファイル名
     */
    const regFileLinkClick = (fileName: string) => {
      regFileLinkClickLog(
        fileName,
        userId.value,
        agCode.value,
        pseudoAgCode.value,
        pseudoUserId.value,
        contentsData.value.contentsId,
        LoginPopupModalConstants.SCREEN_NAME
      );
    };

    /**
     * modal-contentのスクロールをトップに設定
     */
    const scrollToTop = () => {
      if (modalContent.value) {
        modalContent.value.scrollTop = 0; // 要素のスクロールをトップに設定
      }
    };

    return {
      isInitError,
      isDataFetched,
      errorMessage,
      contentsData,
      sanitizedContent,
      isChecked,
      isCheckPaternConfirm,
      isExistsLinkAreaItem,
      isPreviewMode,
      modalContent,
      onClosed,
      onChangeCheckState,
      downloadFiles,
      regLinkClick,
      regFileLinkClick,
    };
  },
});
</script>

<style scoped>
/*
  モーダル設定
*/
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}
.modal-container {
  background: white;
  padding: 30px 80px;
  border-radius: 5px;
  position: relative;
  min-height: 60%; /* モーダルの最大高さを設定 */
  max-height: 80vh; /* モーダルの最大高さを設定 */
  /* overflow-y: auto; */ /* モーダル内でスクロールを有効にする */
  display: flex;
  flex-direction: column;
  height: 80vh; /* モーダルの高さを設定 */
  width: 80%; /* モーダルの幅を設定 */
}
.modal-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
}
.modal-content {
  flex: 1; /* 残りのスペースを占める */
  border-width: 0px;
  margin: 0 auto; /* 中央寄せ */
  flex-direction: column;
  display: flex;
  overflow-y: auto; /* モーダル内でスクロールを有効にする */
  height: 60vh; /* モーダルの高さを設定 */
}
.modal-footer {
  background: none;
  border-top: 1px solid #999;
  flex-direction: column;
  align-items: flex-end;
}
.form-check-label {
  font-size: 18px;
}
/*
  共通CSSカスタマイズ
*/
.ql-editor {
  overflow-y: initial;
  height: auto;
}
.com_checkbox1 {
  display: block;
}
.com_s_button1 {
  font-size: 18px;
  width: 120px;
}
.com_s_button1:disabled {
  background-color: #6c757d;
  border: 1px solid #6c757d;
  color: #fff;
  opacity: 0.65;
}
</style>

<style>
.modal-open {
  overflow: hidden; /* ボディのスクロールを無効化 */
}
</style>
